// src/Components/Common/CustomDatePicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { focusNextField } from '../../../Utils/family-tree/addMemberFieldLimits';

const CustomDatePicker = ({ 
  value, 
  onChange, 
  name,
  label = '',
  format = 'MM/DD/YYYY',
  fullWidth = true,
  required = false,
  maxDate = dayjs(),
  error = false,
  helperText = '',
  autoFocusNextOnComplete = false,
  ...props 
}) => {
  const [internalError, setInternalError] = useState(false);
  const [internalHelperText, setInternalHelperText] = useState('');
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  const [localValue, setLocalValue] = useState(() => {
    // Initialize local value from prop value
    return value ? dayjs(value) : null;
  });
  const lastSyncedValueRef = useRef(value);
  const inputRef = useRef(null);

  // Validate if date is complete (has month, day, and year)
  const validateDate = (dateValue, showError = false) => {
    // Only show errors if field has been touched or if explicitly requested
    if (!showError && !hasBeenTouched) {
      setInternalError(false);
      setInternalHelperText('');
      return true;
    }

    if (!dateValue) {
      if (required && showError) {
        setInternalError(true);
        setInternalHelperText('Date is required');
        return false;
      }
      setInternalError(false);
      setInternalHelperText('');
      return true;
    }

    // Check if date is valid and complete
    const dayjsDate = dayjs(dateValue);
    if (!dayjsDate.isValid()) {
      if (showError) {
        setInternalError(true);
        setInternalHelperText('Invalid date');
      }
      return false;
    }

    // Check if all parts (month, day, year) are present
    const month = dayjsDate.month() + 1; // dayjs months are 0-indexed
    const day = dayjsDate.date();
    const year = dayjsDate.year();

    if (!month || !day || !year) {
      if (showError) {
        setInternalError(true);
        setInternalHelperText('Please select month, day, and year');
      }
      return false;
    }

    setInternalError(false);
    setInternalHelperText('');
    return true;
  };

  const handleChange = (newValue) => {
    // Mark field as touched when user interacts
    if (!hasBeenTouched) {
      setHasBeenTouched(true);
    }

    // Update local value to preserve partial input while typing
    // This is crucial - we always update localValue so the DatePicker shows what user typed
    setLocalValue(newValue);

    // Handle null/cleared value
    if (!newValue) {
      // Clear error if date is cleared and not required
      if (!required) {
        setInternalError(false);
        setInternalHelperText('');
        lastSyncedValueRef.current = null;
        onChange({ 
          target: { 
            name, 
            value: null,
            isValid: false
          } 
        });
      } else if (hasBeenTouched) {
        // If required and cleared, show error
        setInternalError(true);
        setInternalHelperText('Date is required');
        lastSyncedValueRef.current = null;
        onChange({ 
          target: { 
            name, 
            value: null,
            isValid: false
          } 
        });
      }
      return;
    }

    // Check if dayjs object is valid (basic validity check)
    if (!newValue.isValid()) {
      // Invalid dayjs object - don't update parent, but keep localValue for display
      // This allows user to continue typing without losing their input
      // But we still need to send empty value to parent so validation can check
      lastSyncedValueRef.current = null;
      onChange({ 
        target: { 
          name, 
          value: null,
          isValid: false
        } 
      });
      return;
    }

    // Format the date value (even if incomplete) so parent state always has current value
    // This allows validation to check completeness
    const dateValueFormatted = newValue.format("YYYY/MM/DD");
    
    // Validate the date is complete (has month, day, and year)
    const isValid = validateDate(newValue, false); // Don't show error while typing
    
    if (!isValid) {
      // Date is incomplete - still update parent state so validation can check it
      // But mark it as invalid
      lastSyncedValueRef.current = dateValueFormatted;
      onChange({ 
        target: { 
          name, 
          value: dateValueFormatted,
          isValid: false
        } 
      });
      return;
    }

    // Date is valid and complete - update parent state
    lastSyncedValueRef.current = dateValueFormatted;
    
    // Clear any errors since date is now valid
    setInternalError(false);
    setInternalHelperText('');
    
    onChange({ 
      target: { 
        name, 
        value: dateValueFormatted,
        isValid: true
      } 
    });

    if (autoFocusNextOnComplete) {
      requestAnimationFrame(() => {
        focusNextField(inputRef.current);
      });
    }
  };

  const handleBlur = () => {
    // Mark field as touched when user leaves the field
    if (!hasBeenTouched) {
      setHasBeenTouched(true);
    }
    // Validate on blur if field is required and empty
    if (required && !value) {
      setInternalError(true);
      setInternalHelperText('Date is required');
    } else if (value) {
      validateDate(value, true);
    }
  };

  // Convert Date/string value to Day.js object
  // Always use localValue for display to preserve partial input
  const dayjsValue = localValue;

  // Sync local value with prop value when prop value changes externally (e.g., form reset)
  // Only update if the value actually changed externally (not from our own onChange)
  useEffect(() => {
    // Check if value prop changed externally (not from our own update)
    if (value !== lastSyncedValueRef.current) {
      if (value) {
        const parsedValue = dayjs(value);
        if (parsedValue.isValid()) {
          setLocalValue(parsedValue);
          lastSyncedValueRef.current = value;
        }
      } else if (value === null || value === '') {
        setLocalValue(null);
        lastSyncedValueRef.current = value;
      }
    }
  }, [value]);

  // Only validate on mount if value exists and is invalid (for edit mode)
  // Don't show errors initially for empty required fields
  useEffect(() => {
    if (value) {
      // Only validate existing values, but don't show errors unless touched
      validateDate(value, hasBeenTouched);
    } else {
      // Don't show errors for empty fields initially
      setInternalError(false);
      setInternalHelperText('');
    }
  }, [value, hasBeenTouched]); // Added hasBeenTouched to dependencies

  // Use external error/helperText if provided, otherwise use internal
  // Only show internal errors if field has been touched
  // If external helperText is provided, use it exclusively and ignore internal errors
  const hasExternalHelperText = helperText && helperText.trim() !== '';
  const displayError = hasExternalHelperText ? error : (internalError && hasBeenTouched);
  const displayHelperText = hasExternalHelperText ? helperText : (hasBeenTouched ? internalHelperText : '');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={dayjsValue}
        onChange={handleChange}
        onClose={handleBlur}
        format={format}
        required={!!required}
        maxDate={maxDate}
        onError={(reason) => {
          if (reason === "invalidDate" || reason === "incompleteDate") {
            setInternalError(true);
            setInternalHelperText("Complete date is required");
          } else if (!value && required) {
            setInternalError(true);
            setInternalHelperText("Date is required");
          } else {
            setInternalError(false);
            setInternalHelperText("");
          }
        }}
        slotProps={{
          textField: { 
            fullWidth,
            required: !!required,
            error: displayError,
            helperText: displayHelperText,
            onBlur: handleBlur,
            inputRef,
            inputProps: {
              maxLength: format === 'MM/DD/YY' ? 8 : 10,
            },
            sx: {
              '& .MuiPickersInputBase-root': {
                height: '44px',
                minHeight: '44px',
                alignItems: 'center',
              },
              '& .MuiPickersSectionList-section': {
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiPickersSectionList-sectionContent': {
                fontSize: '16px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '4px 0',
                minWidth: 'auto',
              },
              '& .MuiPickersInputBase-sectionAfter, & .MuiPickersInputBase-sectionBefore': {
                color: '#666',
              },
              '& .MuiIconButton-root': {
                padding: '8px',
              },
              '& .MuiPickersSectionList-sectionContent:focus': {
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: displayError ? '#d32f2f' : '#ccc',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: displayError ? '#d32f2f' : '#000',
              },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: displayError ? '#d32f2f' : '#1976d2',
                borderWidth: '2px',
              }
            }
          },
          popper: {
            style: { zIndex: 9999 }
          }
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};

export default CustomDatePicker;